'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react'

import { CollectionFiltersDS } from '@src/components/blocks/collection-filters-ds/collection-filters-ds'
import { LoaderWrapperDS } from '@src/components/blocks/loader-wrapper-ds'
import { SearchResultsDS } from '@src/components/blocks/search-results-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { useSearchResults } from '@src/pages/search/searchResultsSection/hooks/useSearchResults'
import { UpdateQueryParams } from '@src/quince-components/src/atoms/filterSection/menu'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong/somethingWentWrong'

/* eslint-disable i18next/no-literal-string -- Panda design tokens and raw lengths, not user-facing copy */

// Figma DS 2.0 `WidgetSearchTitle` band — desktop (node 5718:34069) / mobile (node 5718:34073).
// `SectionContentContainer` already supplies the full-bleed band + responsive horizontal gutters
// (`space-16` / `space-48`) and the centered `1504px` content frame, so only the block padding
// (mobile `space-20` / desktop `space-24`) is added here. Vertical spacing lives on the outer band.
const headerBandClass = css({
  py: { base: 'space-20', lg: 'space-24' },
})

// Figma `Content` frame — desktop (node 5718:34070) start-aligned / mobile (node 5718:34074)
// center-aligned column with an `space-8` (8px) row gap; wraps long search terms.
const headerContentClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: { base: 'center', lg: 'flex-start' },
  gap: 'space-8',
  wordBreak: 'break-word',
})

// Figma keyword — desktop `title-20` (node 5718:34072) / mobile `title-14` (node 5718:34077);
// each text style supplies Grosa Medium / weight 500 / line-height / letter-spacing, so only
// colour + uppercase + alignment are layered on. neutral/black #21201F.
const titleClass = css({
  w: 'full',
  textStyle: { base: 'title-14', lg: 'title-20' },
  textTransform: 'uppercase',
  textAlign: { base: 'center', lg: 'left' },
  color: 'black',
})

// Figma count — `body-12` (Grosa Regular / size 12 / line-height 1.3) on both breakpoints
// (desktop node 5718:34205 / mobile node 5718:34078). neutral/gray #606060 on desktop,
// neutral/mediumgray #717171 on mobile; centered on mobile.
const countClass = css({
  w: 'full',
  textStyle: 'body-12',
  textAlign: { base: 'center', lg: 'left' },
  color: { base: 'mediumgray', lg: 'gray' },
})

// With no search header above, desktop pads the bar's top so it isn't flush to the band; mobile 0.
const filterSectionHiddenHeaderClass = css({
  pt: { base: '0', lg: 'space-32' },
})

// No wrapper class for the error state, deliberately. The legacy branch passed
// `styles.somethingWentWrongWrapper` from searchResultsSection.module.scss, but
// `.somethingWentWrongWrapper` (margin-top: 15rem) is only defined in the sibling
// searchResults/searchResults.module.scss — so the lookup was `undefined`, classNames dropped it,
// and shoppers have always seen the message flush to the top. Porting the rule here made it real
// for the first time (240px at the DS 16px root), which was a regression, not a fix. See
// STRFRNT-16594: restoring the gap on purpose would be a design decision, and would also need the
// legacy import corrected so both branches agree.

/* eslint-enable i18next/no-literal-string */

// Search has no collection slug; CollectionFiltersDS keys its sessionStorage drawer/collapse
// state on this scope. `/search` matches the desktop `usePathname()` fallback and is absent from
// excludeDepartmentFilter's slug list, so the department facet is preserved (current behavior).
// eslint-disable-next-line i18next/no-literal-string -- route path used as a storage scope key, not user-facing copy
const SEARCH_FILTER_STATE_SCOPE = '/search'

interface SearchResultsSectionDSProps {
  setSearchLoaded: Dispatch<SetStateAction<boolean>>
}

export function SearchResultsSectionDS({ setSearchLoaded }: SearchResultsSectionDSProps) {
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const {
    productCards,
    isLayoutLoading,
    isError,
    searchQuery,
    intersectRef,
    isMoreProductsLoading,
    totalProducts,
    filters,
    selectedFilters,
    updateQuery,
    hideSearchHeader,
  } = useSearchResults(isRecurringItemSubscriptionsExpEnabled)

  useEffect(() => {
    setSearchLoaded(!isLayoutLoading)
  }, [isLayoutLoading, setSearchLoaded])

  const { t } = useTranslation('common')

  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)
  const shouldRenderFilters = totalProducts > 0 || hasAppliedFilters

  const handleResetFilters = useCallback(() => {
    updateQuery({ filter: { type: 'reset' } })
  }, [updateQuery])

  if (isError) {
    return (
      <SomethingWentWrong
        // 200px = legacy's rendered height (20rem on the fluid 10px root at desktop). A raw rem here
        // would resolve against the DS 16px root and reserve 320px instead.
        // eslint-disable-next-line i18next/no-literal-string -- CSS length value, not user-facing copy
        minHeight={'200px'}
      />
    )
  }

  const renderFilterSection = filters.length > 0 && shouldRenderFilters

  return (
    <div>
      {/* eslint-disable-next-line i18next/no-literal-string -- CSS length value */}
      <LoaderWrapperDS showLoader={isLayoutLoading} minHeight={'16rem'} loaderSize={40}>
        {searchQuery && !hideSearchHeader && totalProducts > 0 ? (
          <SectionContentContainer className={headerBandClass} contentClassName={headerContentClass}>
            <h1 className={titleClass} data-testid={TEST_LOCATORS.SEARCH_RESULTS_TITLE}>{`"${searchQuery}"`}</h1>
            <p className={countClass}>
              {`${t('showing')} ${t(totalProducts === 1 ? 'totalitems_item' : 'totalitems_items', {
                totalItems: totalProducts,
              })}`}
            </p>
          </SectionContentContainer>
        ) : null}

        {/* eslint-disable-next-line i18next/no-literal-string -- DOM id used by search-page tests / observers */}
        <div id={'newSearchSection'}>
          {renderFilterSection ? (
            <CollectionFiltersDS
              noOfItems={totalProducts}
              filters={filters}
              selectedFilters={selectedFilters as unknown as Record<string, string[]>}
              updateQuery={updateQuery as (params: UpdateQueryParams) => void}
              skipSorting
              // During a filter-triggered refetch LoaderWrapperDS sets the children to display:none.
              // The bar's Radix popovers portal to body, so a hidden trigger would orphan an open
              // dropdown to (0,0). Forwarding the loading state unmounts the bar (and its popover)
              // for that window, so the dropdown hides cleanly like legacy instead of jumping.
              showShimmer={isLayoutLoading}
              collectionSlug={SEARCH_FILTER_STATE_SCOPE}
              filtersContainerClassName={
                hideSearchHeader || !totalProducts ? filterSectionHiddenHeaderClass : undefined
              }
            />
          ) : null}

          <SearchResultsDS
            searchQuery={searchQuery}
            productCards={productCards}
            intersectRef={intersectRef}
            isMoreProductsLoading={isMoreProductsLoading}
            onResetFilters={handleResetFilters}
            selectedFilters={selectedFilters}
          />
        </div>
      </LoaderWrapperDS>
    </div>
  )
}
